export interface ScaleDef {
  name: string;
  pc: number;
  noten: ReadonlyArray<string>;
  finger: ReadonlyArray<number>;
  vorzeichen: 'ohne' | 'kreuz' | 'b';
}

export const TONLEITER_STUFEN: ReadonlyArray<number> = Object.freeze(
  [0, 2, 4, 5, 7, 9, 11, 12],
);

function scale(def: ScaleDef): Readonly<ScaleDef> {
  return Object.freeze({
    ...def,
    noten: Object.freeze([...def.noten]),
    finger: Object.freeze([...def.finger]),
  });
}

export const SCALES: Readonly<Record<string, Readonly<ScaleDef>>> = Object.freeze({
  C: scale({ name: 'C-Dur', pc: 0, vorzeichen: 'ohne', noten: ['C', 'D', 'E', 'F', 'G', 'A', 'B', 'C'], finger: [1, 2, 3, 1, 2, 3, 4, 5] }),
  G: scale({ name: 'G-Dur', pc: 7, vorzeichen: 'kreuz', noten: ['G', 'A', 'B', 'C', 'D', 'E', 'F♯', 'G'], finger: [1, 2, 3, 1, 2, 3, 4, 5] }),
  D: scale({ name: 'D-Dur', pc: 2, vorzeichen: 'kreuz', noten: ['D', 'E', 'F♯', 'G', 'A', 'B', 'C♯', 'D'], finger: [1, 2, 3, 1, 2, 3, 4, 5] }),
  A: scale({ name: 'A-Dur', pc: 9, vorzeichen: 'kreuz', noten: ['A', 'B', 'C♯', 'D', 'E', 'F♯', 'G♯', 'A'], finger: [1, 2, 3, 1, 2, 3, 4, 5] }),
  E: scale({ name: 'E-Dur', pc: 4, vorzeichen: 'kreuz', noten: ['E', 'F♯', 'G♯', 'A', 'B', 'C♯', 'D♯', 'E'], finger: [1, 2, 3, 1, 2, 3, 4, 5] }),
  B: scale({ name: 'H-Dur', pc: 11, vorzeichen: 'kreuz', noten: ['B', 'C♯', 'D♯', 'E', 'F♯', 'G♯', 'A♯', 'B'], finger: [1, 2, 3, 1, 2, 3, 4, 5] }),
  'F#': scale({ name: 'F♯-Dur', pc: 6, vorzeichen: 'kreuz', noten: ['F♯', 'G♯', 'A♯', 'B', 'C♯', 'D♯', 'E♯', 'F♯'], finger: [2, 3, 1, 2, 3, 4, 1, 2] }),
  F: scale({ name: 'F-Dur', pc: 5, vorzeichen: 'b', noten: ['F', 'G', 'A', 'B♭', 'C', 'D', 'E', 'F'], finger: [1, 2, 3, 4, 1, 2, 3, 4] }),
  Bb: scale({ name: 'B♭-Dur', pc: 10, vorzeichen: 'b', noten: ['B♭', 'C', 'D', 'E♭', 'F', 'G', 'A', 'B♭'], finger: [4, 1, 2, 3, 1, 2, 3, 4] }),
  Eb: scale({ name: 'E♭-Dur', pc: 3, vorzeichen: 'b', noten: ['E♭', 'F', 'G', 'A♭', 'B♭', 'C', 'D', 'E♭'], finger: [3, 1, 2, 3, 4, 1, 2, 3] }),
  Ab: scale({ name: 'A♭-Dur', pc: 8, vorzeichen: 'b', noten: ['A♭', 'B♭', 'C', 'D♭', 'E♭', 'F', 'G', 'A♭'], finger: [3, 4, 1, 2, 3, 1, 2, 3] }),
  Db: scale({ name: 'D♭-Dur', pc: 1, vorzeichen: 'b', noten: ['D♭', 'E♭', 'F', 'G♭', 'A♭', 'B♭', 'C', 'D♭'], finger: [2, 3, 1, 2, 3, 4, 1, 2] }),
});

export function skalaVon(symbol: string): Readonly<ScaleDef> | null {
  if (!symbol.startsWith('scale:')) return null;
  return SCALES[symbol.slice(6)] || null;
}

export function skalaTonklassen(symbol: string): number[] {
  const scaleDef = skalaVon(symbol);
  return scaleDef ? TONLEITER_STUFEN.map(stufe => (scaleDef.pc + stufe) % 12) : [];
}

export function skalenNach(
  vorzeichen: ScaleDef['vorzeichen'],
): string[] {
  return Object.entries(SCALES)
    .filter(([, scaleDef]) => scaleDef.vorzeichen === vorzeichen)
    .map(([key]) => key);
}
